import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import { conditionalObjectMerge } from '@/lib/utils'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CustomerReview,
  CustomerReviewIdInput,
  CustomerReviewIndexFilters,
  CustomerReviewIndexResponse,
  UpdateCustomerReviewInput,
} from './customer-reviews.types'

export interface CustomerReviewOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for calls that act on one review; the Laravel routes require its id. */
export interface CustomerReviewScope extends CustomerReviewOrganizationScope {
  customerReviewId: CustomerReviewIdInput
}

/** The show query key's entries; the shared key builder can't mark an entry required. */
export interface CustomerReviewShowKeyEntries extends CustomerReviewOrganizationScope {
  customerReviewId?: CustomerReviewIdInput
}

/** Query functions read only the key (and the abort signal when the caller passes one). */
type CustomerReviewQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export type CustomerReviewIndexQueryKeyEntries = CustomerReviewOrganizationScope & {
  filters?: CustomerReviewIndexFilters | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

export async function getCustomerReviews({
  queryKey: [{ organizationId, filters = {}, page = 1, pageSize = 20 }],
  signal,
}: CustomerReviewQueryContext<CustomerReviewIndexQueryKeyEntries>) {
  const params = conditionalObjectMerge(
    {
      ...filters,
      page,
      page_size: pageSize,
    },
    {}
  )

  return (
    (await axios.get<CustomerReviewIndexResponse>(
      '/customer-reviews',
      buildAdminRequestConfig({
        organizationId,
        params,
        ...(signal ? { signal } : {}),
      })
    )) || {
      data: [],
      meta: {
        current_page: page,
        page_size: pageSize,
        total: 0,
        last_page: 1,
      },
      summary: {
        average_rating: null,
        review_count: 0,
      },
    }
  )
}

/** Also marks the review's notification read for the current user. */
export async function getCustomerReview({
  queryKey: [{ organizationId, customerReviewId }],
}: CustomerReviewQueryContext<CustomerReviewShowKeyEntries>) {
  return (
    (await axios.get<CustomerReview>(
      `/customer-reviews/${customerReviewId}`,
      buildAdminRequestConfig({ organizationId })
    )) || {}
  )
}

export async function updateCustomerReview({
  organizationId,
  customerReviewId,
  ...values
}: CustomerReviewScope & UpdateCustomerReviewInput) {
  return axios.put<CustomerReview>(
    `/customer-reviews/${customerReviewId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function publishCustomerReview({
  organizationId,
  customerReviewId,
}: CustomerReviewScope) {
  return axios.put<CustomerReview>(
    `/customer-reviews/${customerReviewId}/publish`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function hideCustomerReview({
  organizationId,
  customerReviewId,
}: CustomerReviewScope) {
  return axios.put<CustomerReview>(
    `/customer-reviews/${customerReviewId}/hide`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function rejectCustomerReview({
  organizationId,
  customerReviewId,
}: CustomerReviewScope) {
  return axios.put<CustomerReview>(
    `/customer-reviews/${customerReviewId}/reject`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function deleteCustomerReview({
  organizationId,
  customerReviewId,
}: CustomerReviewScope) {
  return axios.delete<string>(
    `/customer-reviews/${customerReviewId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
